import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  InputAdornment,
  LinearProgress,
  ListItemButton,
  ListItemText,
  ListSubheader,
  styled,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import List from '@mui/material/List';
import { Command } from 'cmdk';
import React, { useCallback, useState } from 'react';
import { groupBy, identity } from 'remeda';
import { useDebounce } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';

const ROOT_GROUP = 'root';
export interface SearchDialogItem {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  group?: string;
  keywords?: string[];
}

type SearchDialogProps<ItemT> = {
  open?: boolean;
  loading?: boolean;
  showProgress?: boolean;
  options: ItemT[];
  onClose?: () => void;
  placeholder?: string;
  dialogStyles?: object;
  inputStyles?: object;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode | false;
  notFoundText?: string;
  loadingText?: string;
  width?: string;
  listItemStyles?: (item: ItemT) => object;
  renderItemStartIcon?: (item: ItemT) => React.ReactNode;
  renderItemEndIcon?: (item: ItemT) => React.ReactNode;
  onSelect?: (item: ItemT) => void;
  dialogContentStyles?: object;
  onSearchChange?: (value: string) => void;
  onSearchChangeDebounce?: number;
  filterHandler?: (value: string, search: string, keywords?: string[]) => number;
  footer?: React.ReactNode;
};

export const StyledList = styled(List, {
  name: 'StyledList',
  shouldForwardProp: (prop) => prop !== 'component' && prop !== 'isMobile',
})<{ component: string; isMobile?: boolean }>(({ theme, isMobile }) => ({
  position: 'relative',
  padding: 0,
  overflowY: 'auto',
  ...(isMobile ? { flexGrow: 1 } : { maxHeight: '50vh' }),
  '& [cmdk-group-heading]': {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0.16))',
    zIndex: 2,
  },
}));

export const SearchDialog = <ItemT extends SearchDialogItem>({
  open = true,
  loading = false,
  showProgress = false,
  options,
  onClose,
  placeholder = 'Search...',
  dialogStyles,
  inputStyles,
  startAdornment,
  endAdornment,
  notFoundText = 'No results found.',
  loadingText = 'Loading...',
  listItemStyles,
  renderItemStartIcon,
  renderItemEndIcon,
  onSelect,
  onSearchChange = identity,
  onSearchChangeDebounce = 0,
  dialogContentStyles,
  filterHandler,
  width = '37.5rem',
  footer,
}: SearchDialogProps<ItemT>) => {
  const { isMobile } = useScreenDimensions();
  const [search, setSearch] = useState('');
  const theme = useTheme();

  const handleOnSearchChange = useDebounce(onSearchChange, onSearchChangeDebounce);

  const handleSearchChange = useCallback(
    (value: string) => {
      const trimmedValue = value?.trim();
      setSearch(trimmedValue);
      handleOnSearchChange(trimmedValue);
    },
    [handleOnSearchChange],
  );

  const renderItem = (item: ItemT, index: number) => (
    <Command.Item
      asChild
      key={item.id || index}
      keywords={item.keywords}
      onSelect={() => {
        onSelect?.(item);
      }}
    >
      <ListItemButton
        component="div"
        {...(options.length === 1 ? { 'aria-selected': true } : {})}
        sx={{
          '&[aria-selected="true"]': {
            backgroundColor: theme.palette.custom.OV.sys.secondaryContainer,
          },
          ...listItemStyles?.(item),
        }}
      >
        {item.icon ?? renderItemStartIcon?.(item)}
        <ListItemText
          title={[item.title, item.description].join(' ')}
          primary={item.title}
          secondary={item.description}
          sx={{
            '& > *': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
        />
        {renderItemEndIcon?.(item)}
      </ListItemButton>
    </Command.Item>
  );

  const groupedOptions = groupBy(options, (item) => item.group || ROOT_GROUP);
  const groupKeys = Object.keys(groupedOptions);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          marginTop: '20vh',
          alignSelf: 'flex-start',
          ...(isMobile && {
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            borderRadius: 0,
            maxHeight: '100%',
            margin: 0,
          }),
          '& [cmdk-root]': {
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            maxHeight: '100%',
          },
          ...dialogStyles,
        },
      }}
    >
      <DialogContent sx={{ padding: 0, ...dialogContentStyles }}>
        <Command
          loop
          shouldFilter={!!filterHandler}
          filter={filterHandler}
          label="Command Menu"
        >
          <Command.Input value={search} onValueChange={handleSearchChange} asChild>
            <FormControl sx={{ position: 'relative', ...(isMobile && { width: '100%' }) }}>
              <TextField
                autoFocus
                autoComplete="off"
                variant="standard"
                fullWidth
                placeholder={placeholder}
                InputProps={{
                  sx: {
                    ...(isMobile ? { width: '100%' } : { maxWidth: width, width }),
                    padding: '0.75rem',
                    ...inputStyles,
                  },
                  startAdornment: (
                    <InputAdornment position="start" sx={{ marginRight: '0.7rem' }}>
                      {startAdornment || <SearchIcon />}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end" sx={{ marginLeft: '0.7rem' }}>
                      {!endAdornment && endAdornment !== false ? (
                        <Button
                          variant="text"
                          onClick={onClose}
                          startIcon={<CloseIcon />}
                          sx={{
                            minWidth: '0',
                            '& .MuiButton-startIcon': {
                              margin: 0,
                            },
                          }}
                        />
                      ) : (
                        endAdornment
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {!!showProgress && (
                <LinearProgress
                  sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '2px' }}
                />
              )}
            </FormControl>
          </Command.Input>
          <StyledList component="div" sx={{ maxWidth: width }} isMobile={isMobile}>
            <Command.List>
              {loading ? (
                <Command.Loading>
                  <Typography variant="subtitle2" textAlign="center" padding={2}>
                    {loadingText}
                  </Typography>
                </Command.Loading>
              ) : (
                <Command.Empty asChild>
                  <Typography variant="subtitle2" align="center" padding={2}>
                    {notFoundText}
                  </Typography>
                </Command.Empty>
              )}
              {groupKeys.length === 1 && groupKeys[0] === ROOT_GROUP
                ? groupedOptions[ROOT_GROUP].map(renderItem)
                : Object.entries(groupedOptions).map(([group, items]) => (
                    <Command.Group
                      key={group}
                      heading={
                        <ListSubheader
                          component="div"
                          disableSticky
                          sx={{
                            lineHeight: '2.26rem',
                            backgroundColor: theme.palette.custom.OV.sys.surfaceBright,
                          }}
                        >
                          {group === ROOT_GROUP ? 'Actions' : group}
                        </ListSubheader>
                      }
                    >
                      {items.map(renderItem)}
                    </Command.Group>
                  ))}
            </Command.List>
          </StyledList>
        </Command>
        {footer}
      </DialogContent>
    </Dialog>
  );
};
